import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CreditCard, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inr = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0);

/**
 * Order Review page (Item 4).
 * Reads ?plan_id=&billing_cycle= from the URL, fetches server-side breakdown,
 * shows current vs new plan, price/discount/tax/total, then hands off to Razorpay.
 */
export default function SubscriptionCheckout() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const planId  = sp.get("plan_id") || "pro";
  const [cycle, setCycle] = useState(sp.get("billing_cycle") === "annual" ? "annual" : "monthly");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/subscription/order-preview?plan_id=${planId}&billing_cycle=${cycle}`)
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [planId, cycle]);

  const pay = async () => {
    setRedirecting(true);

    try {
      const { data } = await api.post("/payments/checkout", {
        plan_id: planId,
        billing_cycle: cycle,
        origin_url: window.location.origin,
      });

      // Free-plan downgrade does not require a gateway.
      if (data.activated) {
        toast.success("Subscription updated");
        nav("/profile?tab=subscription", { replace: true });
        return;
      }

      // Load Razorpay Checkout only when required.
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const existing = document.querySelector(
            'script[data-rayfuel-razorpay="1"]'
          );

          if (existing) {
            existing.addEventListener("load", resolve, { once: true });
            existing.addEventListener(
              "error",
              () => reject(new Error("Unable to load Razorpay")),
              { once: true }
            );
            return;
          }

          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.async = true;
          script.setAttribute("data-rayfuel-razorpay", "1");
          script.onload = resolve;
          script.onerror = () =>
            reject(new Error("Unable to load Razorpay"));
          document.body.appendChild(script);
        });
      }

      if (!window.Razorpay) {
        throw new Error("Razorpay Checkout unavailable");
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency || "INR",
        name: "RayFuel",
        description: `${data.plan_name || planId} · ${cycle}`,
        order_id: data.order_id,

        handler: async (response) => {
          try {
            const verify = await api.post("/payments/verify", {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (!verify.data?.verified) {
              throw new Error("Payment verification failed");
            }

            if (verify.data?.status === "paid") {
              toast.success("Payment successful");

              nav(
                `/payment/success?session_id=${encodeURIComponent(
                  data.order_id
                )}`,
                { replace: true }
              );
              return;
            }

            toast.success("Payment received. Confirming status…");

            nav(
              `/payment/success?session_id=${encodeURIComponent(
                data.order_id
              )}`,
              { replace: true }
            );
          } catch (e) {
            toast.error(formatApiError(e));
            setRedirecting(false);
          }
        },

        modal: {
          ondismiss: () => {
            setRedirecting(false);
          },
        },

        theme: {
          color: "#FF5A1F",
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", (response) => {
        const message =
          response?.error?.description ||
          response?.error?.reason ||
          "Payment failed";

        toast.error(message);
        setRedirecting(false);
      });

      razorpay.open();
    } catch (e) {
      toast.error(formatApiError(e));
      setRedirecting(false);
    }
  };

  if (loading || !preview) {
    return (
      <div className="max-w-lg mx-auto py-16 flex items-center gap-2 justify-center text-[var(--text-muted)]" data-testid="checkout-loading">
        <Loader2 size={16} className="animate-spin" /> Preparing your order…
      </div>
    );
  }

  const isDowngrade = preview.free_downgrade;

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="subscription-checkout-page">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => nav(-1)}
          className="w-9 h-9 border border-[var(--border-line)] hover:bg-[var(--hover)] flex items-center justify-center rounded-lg" aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="ft-overline">STEP 1 · REVIEW &amp; CONFIRM</p>
          <h1 className="font-display text-3xl font-black tracking-tighter">Review your order</h1>
        </div>
      </div>

      <div className="rounded-2xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6 space-y-4" data-testid="order-summary">
        <div className="flex items-center justify-between">
          <div>
            <p className="ft-overline">CURRENT PLAN</p>
            <p className="font-display text-lg font-bold" data-testid="current-plan-name">{preview.current_plan?.name || preview.current_plan?.id || "Free"}</p>
          </div>
          <div className="text-right">
            <p className="ft-overline">NEW PLAN</p>
            <p className="font-display text-lg font-bold text-[#FF5A1F]" data-testid="new-plan-name">{preview.plan_name}</p>
          </div>
        </div>

        {!isDowngrade && (
          <div className="rounded-lg border border-[var(--border-line)] bg-[var(--bg-elevated)] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="ft-overline">BILLING</div>
              <div className="flex gap-2 text-xs" data-testid="billing-cycle-select">
                {["monthly", "annual"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCycle(c)}
                    className={
                      "px-2.5 py-1 rounded-full border " +
                      (cycle === c
                        ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-[#FF5A1F]"
                        : "border-[var(--border-line)] text-[var(--text-muted)] hover:bg-[var(--hover)]")
                    }
                    data-testid={`cycle-${c}`}
                  >
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <Row label="Base price" value={`₹${inr(preview.base_price)}`} testid="row-base" />
            {preview.discount > 0 && <Row label="Annual discount" value={`-₹${inr(preview.discount)}`} tone="ok" testid="row-discount" />}
            {preview.tax_pct > 0 && <Row label={`Tax (${preview.tax_pct}%)`} value={`₹${inr(preview.tax)}`} testid="row-tax" />}
            <hr className="my-3 border-[var(--border-line)]" />
            <Row label="Total payable" value={<span className="text-2xl font-black">₹{inr(preview.total)}</span>} testid="row-total" bold />
            <p className="mt-2 text-xs text-[var(--text-muted)]">Renews {cycle === "monthly" ? "every month" : "annually"} · Cancel anytime.</p>
          </div>
        )}

        {isDowngrade && (
          <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/5 p-4 text-sm text-yellow-200" data-testid="downgrade-warning">
            You're switching back to a free plan. Your existing vehicle records will remain safe, but you may need to remove excess data to stay within Free limits.
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={pay}
        disabled={redirecting}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-base font-bold disabled:opacity-60"
        data-testid="proceed-to-payment-btn"
      >
        {redirecting ? <Loader2 size={16} className="animate-spin"/> : <CreditCard size={16}/>}
        {preview.total > 0 ? `Proceed to payment — ₹${inr(preview.total)}` : "Confirm downgrade"}
      </button>

      <div className="flex items-center justify-center gap-2 text-xs text-[var(--text-muted)]">
        <ShieldCheck size={12} className="text-emerald-400" /> Payments are processed securely by Razorpay. No payment credentials ever touch RayFuel servers.
      </div>
    </div>
  );
}

function Row({ label, value, tone, bold, testid }) {
  const c = tone === "ok" ? "text-emerald-400" : "text-[var(--text-primary)]";
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className="text-[var(--text-muted)]">{label}</span>
      <span className={`${c} ${bold ? "font-black" : "font-semibold"}`}>{value}</span>
    </div>
  );
}
